import React, { useCallback, useEffect, useMemo, useState } from "react";
import DeleteImg from "../../assets/delLogo.png";
import verifyImg from "../../assets/verifyImg.png";
import StaffListModal from "../../components/company-settings/StaffListModal";
import {
  DeleteItem,
  InviteModal,
  SuccessfulDelete,
  SuccessModal,
} from "../../components/modal";
import { SimpleButton } from "../../components/button.jsx/Btn";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "../../api/httpClient";
import { getApiBaseUrl } from "../../data/authStorage";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";
import { calculateLevelCompensation } from "../../utils/compensation";

const EMPTY_COMPENSATION = {
  basicSalary: "",
  housingAllowance: "",
  transportAllowance: "",
  otherAllowance: "",
  recurringDeduction: "",
  taxRate: "",
  pensionRate: "",
};
const COMPENSATION_FIELDS = [
  ["basicSalary", "Monthly basic salary"],
  ["housingAllowance", "Housing allowance"],
  ["transportAllowance", "Transport allowance"],
  ["otherAllowance", "Other allowance"],
  ["recurringDeduction", "Recurring deduction"],
  ["taxRate", "Tax rate (%)"],
  ["pensionRate", "Pension rate (%)"],
];
const salaryNumber = (value) => Number(String(value || "").replace(/[^0-9.-]/g, "")) || 0;
const compensationFrom = (level) =>
  Object.fromEntries(COMPENSATION_FIELDS.map(([key]) => [key,
    key === "basicSalary" && !Number(level?.basicSalary)
      ? salaryNumber(level?.salaryStructure) || ""
      : level?.[key] ?? "",
  ]));
const money = (value) =>
  new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" }).format(Number(value || 0));

const LevelViewDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { step: routeLevelId } = useParams();
  const apiBaseUrl = getApiBaseUrl();
  const levelId = useMemo(
    () => routeLevelId || location.state?.levelId || sessionStorage.getItem("selectedLevelId"),
    [location.state, routeLevelId],
  );
  const cacheKey = useMemo(() => `cehr_level_details_${levelId || "unknown"}`, [levelId]);
  const cachedData = useMemo(() => readSessionCache(cacheKey, { level: null }), [cacheKey]);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openSuccessfulDeleteModal, setOpenSuccessfulDeleteModal] =
    useState(false);
  const [openlevelModal, setOpenLevelModal] = useState(false);
  const [opensucesssModal, setOpensuccessModal] = useState(false);
  const [level, setLevel] = useState(cachedData?.level || null);
  const [isLoading, setIsLoading] = useState(!cachedData?.level);
  const [errorMessage, setErrorMessage] = useState("");
  const [formErrorMessage, setFormErrorMessage] = useState("");
  const [deleteErrorMessage, setDeleteErrorMessage] = useState("");
  const [levelName, setLevelName] = useState("");
  const [careerDetails, setCareerDetails] = useState({ careerRole: "", careerFocus: "", careerResponsibilities: "", showSalaryOnDashboard: false });
  const [compensation, setCompensation] = useState(EMPTY_COMPENSATION);
  const [additionalAllowances, setAdditionalAllowances] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isStaffModalOpen, setIsStaffModalOpen] = useState(false);
  const [staffMembers, setStaffMembers] = useState([]);
  const [isStaffLoading, setIsStaffLoading] = useState(false);
  const [staffErrorMessage, setStaffErrorMessage] = useState("");

  const resolveStaffList = (response) => {
    const payload = response?.data || {};

    if (Array.isArray(payload.staff)) return payload.staff;
    if (Array.isArray(payload.employees)) return payload.employees;
    if (Array.isArray(payload.data)) return payload.data;

    return [];
  };

  const loadLevelStaff = useCallback(async () => {
    if (!apiBaseUrl || !levelId) {
      setStaffErrorMessage("Unable to load staff for this level right now.");
      return;
    }

    try {
      setIsStaffLoading(true);
      setStaffErrorMessage("");

      const response = await axios.get(`${apiBaseUrl}/organizations/levels/${levelId}/staff`);
      setStaffMembers(resolveStaffList(response));
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message || "Unable to load staff for this level. Please try again.";
      setStaffErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
      setStaffMembers([]);
    } finally {
      setIsStaffLoading(false);
    }
  }, [apiBaseUrl, levelId]);

  const openStaffModal = () => {
    setIsStaffModalOpen(true);
    void loadLevelStaff();
  };

  const loadLevel = useCallback(async ({ silent = false } = {}) => {
      if (!apiBaseUrl || !levelId) {
        setErrorMessage(!levelId ? "Select a level from the list first." : "API base URL is not configured.");
        setIsLoading(false);
        return;
      }

      try {
        if (!silent) {
          setIsLoading(true);
        }
        setErrorMessage("");

        const response = await axios.get(`${apiBaseUrl}/organizations/levels/${levelId}`);
        const loadedLevel = response?.data?.level || null;

        setLevel(loadedLevel);
        setLevelName(loadedLevel?.levelName || "");
        setCareerDetails({
          careerRole: loadedLevel?.careerRole || loadedLevel?.levelName || "",
          careerFocus: loadedLevel?.careerFocus || "",
          careerResponsibilities: loadedLevel?.careerResponsibilities || "",
          showSalaryOnDashboard: loadedLevel?.showSalaryOnDashboard === true,
        });
        setCompensation(compensationFrom(loadedLevel));
        setAdditionalAllowances(Array.isArray(loadedLevel?.additionalAllowances) ? loadedLevel.additionalAllowances : []);
        writeSessionCache(cacheKey, { level: loadedLevel });
      } catch (error) {
        const responseMessage =
          error?.response?.data?.message || "Unable to load level details. Please try again.";
        setErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
      } finally {
        setIsLoading(false);
      }
    }, [apiBaseUrl, cacheKey, levelId]);

  useEffect(() => {
    loadLevel({ silent: true });
  }, [loadLevel]);

  useEffect(() => {
    const handleFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        loadLevel({ silent: true });
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [loadLevel]);

  const refreshLevel = async () => {
    if (!apiBaseUrl || !levelId) return;

    const response = await axios.get(`${apiBaseUrl}/organizations/levels/${levelId}`);
    const loadedLevel = response?.data?.level || null;

    setLevel(loadedLevel);
    setLevelName(loadedLevel?.levelName || "");
    setCareerDetails({
      careerRole: loadedLevel?.careerRole || loadedLevel?.levelName || "",
      careerFocus: loadedLevel?.careerFocus || "",
      careerResponsibilities: loadedLevel?.careerResponsibilities || "",
      showSalaryOnDashboard: loadedLevel?.showSalaryOnDashboard === true,
    });
    setCompensation(compensationFrom(loadedLevel));
    setAdditionalAllowances(Array.isArray(loadedLevel?.additionalAllowances) ? loadedLevel.additionalAllowances : []);
  };

  const openModal = () => {
    setFormErrorMessage("");
    setOpenLevelModal(true);
  };

  const openDeleteLevel = () => {
    setDeleteErrorMessage("");
    setOpenDeleteModal(true);
  };
  const cancelDeleteModal = () => {
    setOpenDeleteModal(false);
    setDeleteErrorMessage("");
  };

  const confirmDeleteLevel = async () => {
    if (!apiBaseUrl || !levelId) {
      setDeleteErrorMessage("Unable to delete this level right now.");
      return;
    }

    try {
      setIsDeleting(true);
      setDeleteErrorMessage("");

      await axios.delete(`${apiBaseUrl}/organizations/levels/${levelId}`);

      setOpenDeleteModal(false);
      setOpenSuccessfulDeleteModal(true);
      sessionStorage.removeItem("selectedLevelId");

      setTimeout(() => {
        navigate("/company-settings/levels");
      }, 1200);
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message || "Unable to delete level. Please try again.";
      setDeleteErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const createHandleSuccess = async (ev) => {
    ev.preventDefault();

    if (!apiBaseUrl || !levelId) {
      setFormErrorMessage("Unable to update this level right now.");
      return;
    }

    if (!levelName.trim() || !careerDetails.careerResponsibilities.trim() || compensation.basicSalary === "") {
      setFormErrorMessage("Level name, focus & responsibility, and monthly basic salary are required.");
      return;
    }

    try {
      setIsSaving(true);
      setFormErrorMessage("");

      await axios.patch(`${apiBaseUrl}/organizations/levels/${levelId}`, {
        levelName: levelName.trim(),
        levelDescription: careerDetails.careerResponsibilities.trim(),
        salaryStructure: String(Number(compensation.basicSalary || 0)),
        careerRole: levelName.trim(),
        careerFocus: careerDetails.careerFocus.trim(),
        careerResponsibilities: careerDetails.careerResponsibilities.trim(),
        showSalaryOnDashboard: careerDetails.showSalaryOnDashboard,
        ...Object.fromEntries(
          COMPENSATION_FIELDS.map(([key]) => [key, Number(compensation[key] || 0)]),
        ),
        additionalAllowances: additionalAllowances
          .map((allowance) => ({ name: String(allowance.name || "").trim(), amount: Number(allowance.amount || 0) }))
          .filter((allowance) => allowance.name),
      });

      await refreshLevel();
      setOpenLevelModal(false);
      setOpensuccessModal(true);
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message || "Unable to update level. Please try again.";
      setFormErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const title = level?.levelName || "Level";
  const description = level?.careerResponsibilities || level?.levelDescription || "No level description available yet.";
  const createdAt = level?.createdAt
    ? new Date(level.createdAt).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "-";
  const compensationSummary = calculateLevelCompensation(level);
  const additionalAllowanceTotal = compensationSummary.additionalAllowances;
  const totalMonthlyPay = compensationSummary.grossPay;
  const estimatedNetPay = compensationSummary.illustrativeNetPay;

  return (
    <div className=" font-Outfit">
      <PageIntroNav
        breadcrumbs={[
          { label: "Company Settings", path: "/company-settings" },
          { label: "Levels", path: "/company-settings/levels" },
          { label: title },
        ]}
        title={title}
        subtitle="Level details and management actions."
        fallbackPath="/company-settings/levels"
        backLabel="Back to Levels"
        actions={(
          <div className="flex items-center gap-3">
            <button
              onClick={openModal}
              className="border border-[#1234CD] text-[#1234CD] px-4 py-2 rounded-xl"
            >
              Edit
            </button>
            <button
              onClick={openDeleteLevel}
              className="border border-red-500 text-red-600 px-4 py-2 rounded-xl"
            >
              Delete
            </button>
          </div>
        )}
      />

      {isLoading && !level ? (
        <FetchLoadingState message="Loading level details..." className="min-h-[60vh]" />
      ) : null}

      {errorMessage && !level ? (
        <div className="px-6 py-4 text-sm text-red-600" role="alert">
          {errorMessage}
        </div>
      ) : null}

      {!isLoading && level ? (
        <section className="mx-3 mt-5 overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm sm:mx-6">
          <header className="border-b border-slate-100 px-4 py-4 sm:px-6">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#1233CC]">Level details</p>
            <h2 className="mt-1 text-xl font-bold text-[#071C50]">{title}</h2>
            <p className="mt-2 max-w-5xl text-xs leading-5 text-slate-500 sm:text-sm sm:leading-6">{description}</p>
          </header>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-xs sm:text-sm">
              <thead className="bg-[#071C50] text-[10px] uppercase tracking-wide text-white">
                <tr><th className="px-4 py-3">Field</th><th className="px-4 py-3">Configured value</th><th className="px-4 py-3">Field</th><th className="px-4 py-3">Configured value</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-600">
                {[
                  ["Date created", createdAt, "Total staff", level?.totalStaff ?? 0],
                  ["Level", level?.levelName || "—", "Level name", level?.levelName || "—"],
                  ["Focus", level?.careerFocus || "—", "Salary visible to employees", level?.showSalaryOnDashboard ? "Yes" : "No"],
                  ["Monthly basic salary", money(level?.basicSalary || salaryNumber(level?.salaryStructure)), "Transport allowance", money(level?.transportAllowance)],
                  ["Housing allowance", money(level?.housingAllowance), "Other allowance", money(level?.otherAllowance)],
                  ["Recurring deduction", money(level?.recurringDeduction), "Additional allowances total", money(additionalAllowanceTotal)],
                  ["Tax rate", `${Number(level?.taxRate || 0)}%`, "Pension rate", `${Number(level?.pensionRate || 0)}%`],
                ].map((row, index) => <tr key={row[0]} className={index % 2 ? "bg-slate-50/60" : "bg-white"}><th className="whitespace-nowrap px-4 py-3 font-semibold text-[#071C50]">{row[0]}</th><td className="px-4 py-3">{row[1]}</td><th className="whitespace-nowrap px-4 py-3 font-semibold text-[#071C50]">{row[2]}</th><td className="px-4 py-3">{row[3]}</td></tr>)}
                <tr className="bg-[#071C50] text-white"><th className="px-4 py-4 text-sm font-bold">Total monthly pay</th><td className="px-4 py-4 text-base font-bold">{money(totalMonthlyPay)}</td><th className="px-4 py-4 text-sm font-bold">Illustrative net pay</th><td className="px-4 py-4 text-base font-bold">{money(estimatedNetPay)}</td></tr>
                <tr className="bg-blue-50/40"><td colSpan={4} className="px-4 py-2 text-xs text-slate-500">Illustration uses level defaults. Final payroll can differ after statutory PAYE rules, attendance, employee overrides, and payroll components.</td></tr>
                <tr className="bg-blue-50/40"><th className="px-4 py-3 align-top font-semibold text-[#071C50]">Focus &amp; responsibility</th><td colSpan={3} className="whitespace-pre-wrap px-4 py-3 leading-5">{level?.careerResponsibilities || "—"}</td></tr>
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {!isLoading && level ? (
        <div className="px-6 mt-6 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={openStaffModal}
            className="px-4 py-2 rounded-xl bg-[#1234CD] text-white font-medium"
          >
            View Level Staff
          </button>
        </div>
      ) : null}

      <DeleteItem
        isOpen={openDeleteModal}
        onClose={cancelDeleteModal}
      >
        <div>
          <img
            className="mx-auto h-16 w-16 object-contain animate-pulse"
            src={DeleteImg}
            alt="verify del"
          />

          <h1 className="text-lg text-center text-[#000000] font-bold">
            Continue to delete this level?
          </h1>
          <p className="text-sm text-center mt-2">Action cannot be undone</p>
          {deleteErrorMessage && (
            <p className="text-sm text-center mt-3 text-red-600" role="alert">
              {deleteErrorMessage}
            </p>
          )}
        </div>
        <div className="mt-5 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={cancelDeleteModal}
            disabled={isDeleting}
            className="w-[110px] rounded-[10px] border-2 border-[#1233CC] px-4 py-2 font-medium text-[#1233CC] disabled:cursor-not-allowed disabled:opacity-50"
          >
            No
          </button>
          <button
            type="button"
            className="w-[110px] rounded-[10px] bg-[#1233CC] px-4 py-2 text-[16px] font-medium text-white disabled:cursor-wait disabled:opacity-60"
            onClick={confirmDeleteLevel}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Yes"}
          </button>
        </div>
      </DeleteItem>

      <SuccessfulDelete
        isOpen={openSuccessfulDeleteModal}
        onClose={() => setOpenSuccessfulDeleteModal(false)}
      >
        <div>
          <img
            src={verifyImg}
            alt=" verify delete"
            className=" w-20 animate-ping mx-auto"
          />
          <div className=" text-center mt-6">
            <h2 className="text-black text-[20px] leading-[27px] font-bold mb-2">
              Level deleted
            </h2>
            <p className="text-[#74777B] text-[14px] leading-[18.9px]">
              The level was removed successfully.
            </p>
          </div>
        </div>
      </SuccessfulDelete>

      <InviteModal
        isOpen={openlevelModal}
        onClose={() => setOpenLevelModal(false)}
      >
        <div className="my-8">
          <div>
            <p className=" text-2xl font-semibold text-black font-Outfit">
              Edit Level
            </p>
          </div>
          <form
            className="my-12"
            onSubmit={createHandleSuccess}
          >
            {formErrorMessage && (
              <p className="mb-4 text-sm text-red-600" role="alert">
                {formErrorMessage}
              </p>
            )}

            <div>
              <label
                htmlFor="level-name"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh"
              >
                Level Name
              </label>
              <input
                type="text"
                id="level-name"
                placeholder="Title"
                value={levelName}
                onChange={(ev) => setLevelName(ev.target.value)}
                className="w-full p-2 text-center mb-2  rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div>
              <label
                htmlFor="level-description"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh"
              >
                Focus &amp; responsibility
              </label>

              <textarea
                id="level-description"
                placeholder="Describe the focus and responsibilities for this level"
                value={careerDetails.careerResponsibilities}
                onChange={(ev) => setCareerDetails((current) => ({ ...current, careerResponsibilities: ev.target.value }))}
                rows={4}
                wrap="soft"
                className="w-full p-2 min-h-36 mb-4 resize-y whitespace-pre-wrap break-words rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div className="mt-5 border-t border-slate-100 pt-5">
              <h3 className="font-semibold text-[#071C50]">Employee career preview</h3>
              <p className="mt-1 text-xs text-slate-500">Level name is used consistently across the employee career profile. Focus and responsibility is the shared level description.</p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <label className="text-sm font-medium text-slate-600">Level name<input readOnly value={levelName} className="mt-2 w-full cursor-not-allowed rounded-xl border-2 border-[#F3F5F5] bg-slate-50 px-3 py-3 text-slate-500 outline-none" /></label>
                <label className="text-sm font-medium text-slate-600">Focus<input maxLength={160} value={careerDetails.careerFocus} onChange={(event) => setCareerDetails((current) => ({ ...current, careerFocus: event.target.value }))} placeholder="e.g. Apply" className="mt-2 w-full rounded-xl border-2 border-[#F3F5F5] px-3 py-3 outline-none focus:border-[#1234CD]" /></label>
              </div>
              <label className="mt-4 flex cursor-pointer items-center justify-between gap-4 rounded-xl border border-slate-100 bg-slate-50 p-4"><span><span className="block text-sm font-semibold text-[#071C50]">Show monthly salary to employees</span><span className="mt-1 block text-xs text-slate-500">When off, the salary column is completely hidden from the employee preview and details.</span></span><input type="checkbox" checked={careerDetails.showSalaryOnDashboard} onChange={(event) => setCareerDetails((current) => ({ ...current, showSalaryOnDashboard: event.target.checked }))} className="h-5 w-5 accent-[#1234CD]" /></label>
            </div>

            <div className="mt-5 border-t border-slate-100 pt-5">
              <h3 className="font-semibold text-[#071C50]">Payroll defaults</h3>
              <p className="mt-1 text-xs text-slate-500">
                These values automatically apply to staff assigned to this level unless individually overridden.
              </p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                {COMPENSATION_FIELDS.map(([key, label]) => (
                  <label key={key} className="text-sm font-medium text-slate-600">
                    {label}
                    <input
                      disabled={isSaving}
                      required={key === "basicSalary"}
                      type="number"
                      min="0"
                      max={key === "taxRate" || key === "pensionRate" ? "100" : undefined}
                      step="0.01"
                      value={compensation[key]}
                      onChange={(event) =>
                        setCompensation((current) => ({ ...current, [key]: event.target.value }))
                      }
                      className="mt-2 w-full rounded-xl border-2 border-[#F3F5F5] px-3 py-3 outline-none focus:border-[#1234CD] disabled:bg-slate-50"
                    />
                  </label>
                ))}
              </div>
              <div className="mt-5 rounded-xl border border-slate-100 bg-slate-50 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div><h4 className="text-sm font-semibold text-[#071C50]">Additional allowances</h4><p className="text-xs text-slate-500">Custom allowances inherited by everyone assigned to this level.</p></div>
                  <button disabled={isSaving} type="button" onClick={() => setAdditionalAllowances((items) => [...items, { name: "", amount: "" }])} className="shrink-0 rounded-lg bg-[#1234CD] px-3 py-2 text-xs font-semibold text-white disabled:opacity-60">+ Add allowance</button>
                </div>
                <div className="mt-3 space-y-3">{additionalAllowances.map((allowance, index) => <div key={index} className="grid gap-2 sm:grid-cols-[1fr_160px_auto]"><input disabled={isSaving} required placeholder="Allowance name" value={allowance.name || ""} onChange={(event) => setAdditionalAllowances((items) => items.map((item, itemIndex) => itemIndex === index ? { ...item, name: event.target.value } : item))} className="rounded-xl border-2 border-white px-3 py-3 outline-none focus:border-[#1234CD]" /><input disabled={isSaving} required type="number" min="0" step="0.01" placeholder="Amount" value={allowance.amount ?? ""} onChange={(event) => setAdditionalAllowances((items) => items.map((item, itemIndex) => itemIndex === index ? { ...item, amount: event.target.value } : item))} className="rounded-xl border-2 border-white px-3 py-3 outline-none focus:border-[#1234CD]" /><button disabled={isSaving} type="button" onClick={() => setAdditionalAllowances((items) => items.filter((_, itemIndex) => itemIndex !== index))} className="rounded-xl px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50">Remove</button></div>)}</div>
              </div>
            </div>

            <div className=" flex justify-center gap-12 mt-8 items-end">
              <SimpleButton
                onClick={() => setOpenLevelModal(false)}
                type="button"
                className="font-[700] font-satoshi  leading-[21.6px] flex justify-center items-center border w-40 rounded-xl  py-[16px] border-[#1234CD] text-[#1234CD] text-[16px] "
              >
                Cancel
              </SimpleButton>

              <SimpleButton
                type="submit"
                className="font-[700] font-satoshi  leading-[21.6px] flex justify-center items-center w-40 rounded-xl text-white py-[16px] bg-[#1234CD] text-[16px] disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={isSaving}
              >
                <span>{isSaving ? "Updating..." : "Update"}</span>
              </SimpleButton>
            </div>
          </form>
        </div>
      </InviteModal>

      <SuccessModal
        isOpen={opensucesssModal}
        onClose={() => setOpensuccessModal(false)}
      />

      <StaffListModal
        isOpen={isStaffModalOpen}
        onClose={() => setIsStaffModalOpen(false)}
        title={`${title} Staff`}
        subtitle="Staff assigned to this level."
        staff={staffMembers}
        loading={isStaffLoading}
        error={staffErrorMessage}
        onRefresh={loadLevelStaff}
      />
    </div>
  );
};

export default LevelViewDetails;
